import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import { FreeMode } from 'swiper/modules';

const Rovoconnect = () => {
  return (
    <div id="main-wrapper">
      <Header />

      <div id="content" role="main">
        {/* Hero Section */}
        <section
          className="hero-wrap"
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: "url('/images/projects/rovovision.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: '70px' }}>
            <div className="row">
              <div className="col-12 text-center text-white">
                <h1 className="text-16 font-weight-600 mb-3 text-white">RovoConnect</h1>
                <p className="lead text-white-100 mb-0">
                  Real-Time Manufacturing Intelligence and Predictive Maintenance
                </p>
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1 }}></div>
        </section>

        {/* Blog Intro */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row align-items-center mb-5 g-4 flex-column-reverse flex-md-row"> 
              <div className="col-md-6 mb-4 mb-md-0"> 
                <img 
                  src="/images/projects/PRE.jpeg" 
                  alt="Robots on Factory Floor" 
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '350px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-3">RovoConnect: Real-Time Manufacturing Intelligence</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  In modern manufacturing, efficiency, uptime, and quality are critical. Yet, robots, PLCs, and industrial machines often operate in silos, limiting real-time data visibility and predictive insights. RovoConnect, a universal industrial gateway, bridges this gap by providing a unified edge platform to monitor, control, and optimize robots, PLCs, and multi-vendor automation systems—delivering actionable intelligence directly from the factory floor.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What is RovoConnect */}
        <section className="py-5 bg-gary text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">What is RovoConnect?</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  RovoConnect is an edge computing solution that connects multiple industrial robots, PLCs, and machines directly via TCP/IP, without requiring intermediate modules or proprietary hardware. Key features include:
                </p>
              </div>
            </div>

            <div className="row g-4">
              {[
                { title: "Unified Dashboard", text: "Get an eagle-eye view of the shop floor in one place.", icon: "bi bi-display", img: "/images/projects/UNI.jpeg" },
                { title: "Predictive Maintenance", text: "Analyze robots and PLCs behavior to anticipate failures before they happen.", icon: "bi bi-activity", img: "/images/projects/PREE.jpeg" },
                { title: "Direct Robot Communication", text: "Works with any TCP/IP-compatible robot, hardware-agnostic.", icon: "bi bi-robot", img: "/images/projects/dir.jpeg" },
                { title: "Real-Time Insights", text: "Tracks every movement, cycle, and parameter for better quality and efficiency.", icon: "bi bi-clock", img: "/images/projects/pp.jpeg" },
              ].map((feature, idx) => (
                <div className="col-12 col-md-6 col-lg-6 mb-4" key={idx}>
                  <div className="card h-100 border-0 shadow-lg rounded-4 overflow-hidden">
                    <img 
                      src={feature.img} 
                      alt={feature.title} 
                      className="card-img-top"
                      style={{ height: '250px', objectFit: 'cover', width: '100%' }} 
                    />
                    <div className="card-body">
                      <div className="mb-2 text-primary">
                        <i className={`${feature.icon} fs-2`}></i>
                      </div>
                      <h5 className="fw-bold mb-3">{feature.title}</h5>
                      <p className="text-muted mb-0">{feature.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Impact */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Business Impact</h2>
                <p className="text-black" style={{ fontSize: "1.50rem" }}>
                  RovoConnect helps manufacturers improve efficiency, uptime, and decision-making.
                </p>
                <ul className="list-unstyled text-muted fs-20">
                  <li className="mb-1"> Reduce unplanned downtime and maintenance costs</li>
                  <li className="mb-1"> Improve Overall Equipment Effectiveness (OEE)</li>
                  <li className="mb-1"> Enhance product quality and traceability</li>
                  <li className="mb-1"> Optimize production throughput and energy usage</li>
                  <li className="mb-2"> Make data-driven decisions with actionable insights</li>
                </ul>
              </div>

              <div className="col-md-6">
                <img
                  src="/images/projects/imp1.jpeg"
                  alt="Business Impact"
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Features & Capabilities */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">Key Features & Capabilities</h2>
                <p className="text-muted mb-5">
                  RovoConnect maximizes efficiency, visibility, and predictive control across your manufacturing floor.
                </p>
              </div>
            </div>

            {/* Horizontal scrollable container */}
            <div
              className="d-flex flex-row flex-nowrap"
              style={{
                gap: "1rem",
                paddingBottom: "1rem",
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              onWheel={(e) => {
                e.currentTarget.scrollLeft += e.deltaY;
              }}
            >
              <style>
                {`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                  @media (max-width: 768px) {
                    .card {
                      min-width: 250px !important;
                      height: auto !important;
                    }
                    .card img {
                      height: 180px !important;
                    }
                  }
                `}
              </style>

              {[
                { title: "Multi-Robot Support", text: "One edge device can handle multiple robots simultaneously.", icon: "bi bi-collection", img: "/images/projects/mul.jpeg" },
                { title: "Live Metrics", text: "Joint angles, velocities, temperatures, cycle times, vibration, and power usage.", icon: "bi bi-speedometer2", img: "/images/projects/live.jpeg" },
                { title: "Predictive Analytics", text: "AI models on the edge forecast failures 15–30 days in advance.", icon: "bi bi-bar-chart", img: "/images/projects/pred.jpeg" },
                { title: "Remote Commands", text: "Update recipes, reset errors, or schedule maintenance from anywhere.", icon: "bi bi-controller", img: "/images/projects/remote.jpeg" },
                { title: "Protocol Agnostic", text: "Supports TCP/IP, Modbus, EtherNet/IP, and more.", icon: "bi bi-layers", img: "/images/projects/protocol.jpeg" },
                { title: "Decision-Making", text: "Make smarter decisions using real-time insights from robots and PLCs.", icon: "bi bi-layers", img: "/images/projects/dic.jpeg" },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="card flex-shrink-0 border-0 shadow-lg rounded-4 overflow-hidden"
                  style={{ width: "300px" }}
                >
                  <img
                    src={feature.img}
                    alt={feature.title}
                    className="card-img-top"
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                  <div className="card-body">
                    <div className="mb-2 text-primary">
                      <i className={`${feature.icon} fs-2`}></i>
                    </div>
                    <h5 className="fw-bold mb-2">{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose RovoConnect */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <img
                  src="/images/projects/why1.jpeg"
                  alt="Why Choose RovoConnect"
                  className="img-fluid rounded-4 shadow-lg mb-4"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Why Choose RovoConnect?</h2>
                <p className="text-muted mb-4">
                  RovoConnect combines cutting-edge technology and universal connectivity to deliver actionable insights and operational efficiency.
                </p>
                <ul className="list-unstyled text-muted fs-6">
                  <li className="mb-3"><strong>Edge-First Architecture:</strong> Minimal latency and no cloud dependency for core operations.</li>
                  <li className="mb-3"><strong>Universal Gateway:</strong> Works with industrial robots and PLCs without requiring proprietary software.</li>
                  <li className="mb-3"><strong>AI-Powered:</strong> Built-in predictive maintenance and anomaly detection.</li>
                  <li className="mb-3"><strong>Seamless Integration:</strong> Connects OT systems (robots, PLCs, SCADA) with IT platforms (ERP, MES, CRM).</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion & CTA */}
        <section className="py-5 bg-dark text-white text-center">
          <div className="container">
            <p className="lead mb-4 mx-auto" style={{ maxWidth: '800px' }}>
              RovoConnect is not just a gateway; it’s a manufacturing intelligence platform that transforms raw robot and PLC data into actionable insights, empowering manufacturers to operate smarter, faster, and more efficiently. From predictive maintenance to real-time control, RovoConnect ensures your factory floor is always optimized and ready for the challenges of Industry 4.0.
            </p>
            <a href="/contact" className="btn btn-primary btn-lg rounded-pill px-5">Inquire About Partnership</a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Rovoconnect;
